import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { formatDisplayDate } from '../utils/dateUtils';
import Snackbar from '../components/Snackbar';
import './UserProfile.css';

const UserProfile = () => {
  const { phoneNumber: userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [userData, setUserData] = useState({});
  const [businessData, setBusinessData] = useState({});
  const [politicalData, setPoliticalData] = useState({});
  const [subscriptionData, setSubscriptionData] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [applyingPlan, setApplyingPlan] = useState(false);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchUserData();
  }, []);

  // Update remaining days when subscription tab is opened
  useEffect(() => {
    if (activeTab === 'subscription' && Object.keys(subscriptionData).length > 0 && userData.mobileNumber) {
      updateRemainingDays(userData.mobileNumber, subscriptionData);
    }
  }, [activeTab]);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch main user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      let userPhoneNumber = null;
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        userPhoneNumber = data.mobileNumber;

      }

      // Only fetch business and political data if we have a phone number
      if (userPhoneNumber) {
        // Fetch business profile from specific document path
        try {
          
          const businessDoc = await getDoc(doc(db, 'users', userPhoneNumber, 'business', 'profile'));
          if (businessDoc.exists()) {
            const data = businessDoc.data();
            setBusinessData(data);

            // Debug business logo path
            if (data.businessLogoPath) {
              if (data.businessLogoPath.startsWith('content://')) {
                console.warn('⚠️ Business logo contains content:// URI (mobile app issue):', data.businessLogoPath);
              } else if (!data.businessLogoPath.startsWith('http')) {
                console.warn('⚠️ Business logo path may be invalid:', data.businessLogoPath);
              } else {
                
              }
            }
          } else {
            
          }
        } catch (error) {
          
        }

        // Fetch political profile from specific document path
        try {
          
          const politicalDoc = await getDoc(doc(db, 'users', userPhoneNumber, 'political', 'profile'));
          if (politicalDoc.exists()) {
            const data = politicalDoc.data();
            setPoliticalData(data);
            
          } else {
            
          }
        } catch (error) {
          
        }

        // Fetch subscription data
        try {
          
          const subscriptionDoc = await getDoc(doc(db, 'users', userPhoneNumber, 'subscription', 'plan'));
          if (subscriptionDoc.exists()) {
            const data = subscriptionDoc.data();
            setSubscriptionData(data);

            // Update remaining days in Firestore
            await updateRemainingDays(userPhoneNumber, data);
          } else {
            
          }
        } catch (error) {
          
        }
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const handleGoBack = () => {
    navigate('/admin/users');
  };

  const handleCustomFrame = () => {
    if (!userId) return;
    navigate(`/admin/custom-frame/${userId}`);
  };

  const handleCustomVideoFrame = () => {
    if (!userId) return;
    navigate(`/admin/custom-video-frame/${userId}`);
  };

  // Helper function to format date as DD-MM-YYYY
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : date.toDate();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const updateRemainingDays = async (phoneNumber, subscriptionData) => {
    try {
      if (!subscriptionData.expiryDate) return;

      const now = new Date();
      const expiryDate = subscriptionData.expiryDate.toDate();
      const remainingDays = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      const isExpired = remainingDays <= 0;

      // Update Firestore with remaining days and status
      const subscriptionRef = doc(db, 'users', phoneNumber, 'subscription', 'plan');
      await updateDoc(subscriptionRef, {
        remainingDays: remainingDays,
        status: isExpired ? 'expired' : 'active',
        lastChecked: Timestamp.fromDate(now)
      });

      // Update local state
      setSubscriptionData(prev => ({
        ...prev,
        remainingDays: remainingDays,
        status: isExpired ? 'expired' : 'active',
        lastChecked: Timestamp.fromDate(now)
      }));
    } catch (error) {
      console.error('Error updating remaining days:', error);
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ show: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar({ show: false, message: '', type: 'success' });
  };

  const handleApplyPlan = async () => {
    if (!selectedPlan || !userData.mobileNumber) {
      showSnackbar('Please select a plan', 'warning');
      return;
    }

    try {
      setApplyingPlan(true);

      const now = new Date();
      let expiryDate = new Date();
      let startDate = now;
      let isExtension = false;
      
      // Check if user has existing active subscription
      if (Object.keys(subscriptionData).length > 0 && subscriptionData.expiryDate) {
        const existingExpiry = subscriptionData.expiryDate.toDate();
        const existingRemainingDays = Math.ceil((existingExpiry - now) / (1000 * 60 * 60 * 24));
        
        // If existing plan is still active (not expired), extend from existing expiry date
        if (existingRemainingDays > 0) {
          expiryDate = new Date(existingExpiry);
          isExtension = true;
          
        }
      }
      
      // Add new plan duration to expiry date
      if (selectedPlan.duration === 7) {
        expiryDate.setDate(expiryDate.getDate() + 7);
      } else if (selectedPlan.duration === 180) {
        expiryDate.setDate(expiryDate.getDate() + 180);
      } else if (selectedPlan.duration === 365) {
        expiryDate.setDate(expiryDate.getDate() + 365);
      }

      // Calculate total remaining days
      const totalRemainingDays = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

      // Track how many times this plan was purchased
      let planCount = 1;
      if (isExtension && subscriptionData.planName === selectedPlan.name) {
        // Same plan purchased again, increment count
        planCount = (subscriptionData.planCount || 1) + 1;
      } else if (isExtension) {
        // Different plan, reset count to 1
        planCount = 1;
      }

      const subscriptionPlanData = {
        planName: selectedPlan.name,
        planCount: planCount,
        price: selectedPlan.price,
        duration: selectedPlan.duration,
        remainingDays: totalRemainingDays,
        startDate: isExtension ? subscriptionData.startDate : Timestamp.fromDate(startDate),
        expiryDate: Timestamp.fromDate(expiryDate),
        status: 'active',
        appliedBy: 'admin',
        appliedAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        lastChecked: Timestamp.fromDate(now),
        isExtended: isExtension,
        lastExtendedPlan: isExtension ? selectedPlan.name : null
      };

      // Store in Firebase: users/{phoneNumber}/subscription/plan
      await setDoc(
        doc(db, 'users', userData.mobileNumber, 'subscription', 'plan'),
        subscriptionPlanData
      );

      setSubscriptionData(subscriptionPlanData);
      setSelectedPlan(null);
      
      const message = isExtension 
        ? `${selectedPlan.name} added to existing subscription! New Expiry: ${formatDate(expiryDate)} (${totalRemainingDays} days)`
        : `${selectedPlan.name} applied successfully! Expiry: ${formatDate(expiryDate)}`;
      
      showSnackbar(message, 'success');
    } catch (error) {
      console.error('Error applying subscription plan:', error);
      showSnackbar('Failed to apply subscription plan. Please try again.', 'error');
    } finally {
      setApplyingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading user profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="page-header user-profile-header">
        <div className="user-profile-title">
          <div className="user-avatar">
            {userData.profileImageUrl ? (
              <img src={userData.profileImageUrl} alt={userData.name} />
            ) : (
              <div className="avatar-placeholder">
                {userData.name?.charAt(0)?.toUpperCase() || '👤'}
              </div>
            )}
          </div>
          <div className="user-details">
            <h2>{userData.name || 'User Profile'}</h2>
            <p className="user-phone">{userData.mobileNumber}</p>
          </div>
          {Object.keys(subscriptionData).length > 0 && (() => {
            const now = new Date();
            const expiryDate = subscriptionData.expiryDate?.toDate();
            const remainingDays = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : 0;
            const isExpired = remainingDays <= 0;
            
            return (
              <div className="active-plan-badge">
                <span className="plan-name">{subscriptionData.planName}</span>
                <span className={`plan-status ${isExpired ? 'expired' : 'active'}`}>
                  {isExpired ? 'Expired' : `${remainingDays} days left`}
                </span>
              </div>
            );
          })()}
        </div>
        <div className="user-profile-actions">
          <button onClick={handleGoBack} className="btn btn-secondary">
            BACK TO USERS
          </button>
          <button className="btn btn-primary" onClick={handleCustomFrame}>
            CUSTOM FRAME
          </button>
          <button className="btn btn-primary" onClick={handleCustomVideoFrame}>
            CUSTOM VIDEO FRAME
          </button>
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-tabs">
          <button 
            className={`tab-btn profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            MY PROFILE
          </button>
          <button 
            className={`tab-btn business-tab ${activeTab === 'business' ? 'active' : ''}`}
            onClick={() => setActiveTab('business')}
          >
            BUSINESS PROFILE
          </button>
          <button 
            className={`tab-btn political-tab ${activeTab === 'political' ? 'active' : ''}`}
            onClick={() => setActiveTab('political')}
          >
            POLITICAL PROFILE
          </button>
          <button 
            className={`tab-btn subscription-tab ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            SUBSCRIPTION
          </button>
        </div>

        <div className="tab-content">
          {/* My Profile Tab */}
          {activeTab === 'profile' && (
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '0',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              <h3 style={{
                background: '#f8fafc',
                margin: '0',
                padding: '32px 40px',
                fontSize: '1.75rem',
                fontWeight: '600',
                color: '#1e293b',
                borderBottom: '1px solid #e5e7eb'
              }}>Personal Information</h3>
              
              <div style={{ padding: '40px', background: '#ffffff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>NAME</span>
                    <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{userData.name || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ADDRESS</span>
                    <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{userData.address || 'Not provided'}</span>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MOBILE NUMBER</span>
                    <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{userData.mobileNumber || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>BIRTHDAY</span>
                    <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{formatDisplayDate(userData.birthday || userData.birthDate) || 'Not provided'}</span>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '40px', marginBottom: '0' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>PINCODE</span>
                    <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{userData.pincode || userData.other || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Business Profile Tab */}
          {activeTab === 'business' && (
            <div className="profile-section">
              {Object.keys(businessData).length > 0 ? (
                <div style={{
                  background: '#ffffff',
                  borderRadius: '24px',
                  padding: '0',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  maxWidth: '900px',
                  margin: '0 auto'
                }}>
                  <h3 style={{
                    background: '#f8fafc',
                    margin: '0',
                    padding: '32px 40px',
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#1e293b',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Business Information</h3>
                  
                  <div style={{ padding: '40px', background: '#ffffff' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>BUSINESS NAME</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.name || 'Not provided'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TAGLINE</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.tagline || 'Not provided'}</span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>BUSINESS TYPE</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.businessType || 'Not provided'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>EMAIL</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.email || 'Not provided'}</span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '32px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MOBILE NUMBER</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.mobileNo || 'Not provided'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>WHATSAPP NUMBER</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>{businessData.whatsappNo || 'Not provided'}</span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '40px', marginBottom: '0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>WEBSITE</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '500', color: '#1e293b', lineHeight: '1.5' }}>
                          {businessData.website ? (
                            <a href={`https://${businessData.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1e40af', textDecoration: 'none', fontWeight: '500' }}>
                              {businessData.website}
                            </a>
                          ) : (
                            'Not provided'
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-profile">
                  <div className="no-profile-icon">💼</div>
                  <h4>No Business Profile</h4>
                  <p>No business profile data found.</p>
                </div>
              )}
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="subscription-section" style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '0',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              maxWidth: '900px',
              margin: '0 auto',
              maxHeight: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column'
            }}>
                <h3 style={{
                  background: '#f8fafc',
                  margin: '0',
                  padding: '10px 16px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#1e293b',
                  borderBottom: '1px solid #e5e7eb',
                  flexShrink: 0
                }}>Subscription Plans</h3>
                
                <div style={{ padding: '12px 16px', background: '#ffffff', overflowY: 'auto', flex: 1 }}>
                  {/* Current Subscription Status */}
                  {Object.keys(subscriptionData).length > 0 && (() => {
                    // Calculate remaining days
                    const now = new Date();
                    const expiryDate = subscriptionData.expiryDate?.toDate();
                    const remainingDays = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : 0;
                    const isExpired = remainingDays <= 0;
                    
                    return (
                      <div style={{
                        background: isExpired ? '#fef2f2' : '#f0fdf4',
                        border: isExpired ? '1px solid #fca5a5' : '1px solid #86efac',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        marginBottom: '10px'
                      }}>
                        <h4 style={{ margin: '0 0 6px 0', color: isExpired ? '#dc2626' : '#166534', fontSize: '0.85rem', fontWeight: '600' }}>
                          Current Subscription {isExpired && '(Expired)'}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>PLAN</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
                              {subscriptionData.planName}
                              {subscriptionData.planCount > 1 && (
                                <span style={{ color: '#1e40af', marginLeft: '8px' }}>x {subscriptionData.planCount}</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>PRICE</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>₹{subscriptionData.price}</p>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>START DATE</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#1e293b' }}>
                              {formatDate(subscriptionData.startDate)}
                            </p>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>EXPIRY DATE</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#1e293b' }}>
                              {formatDate(subscriptionData.expiryDate)}
                            </p>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>REMAINING DAYS</span>
                            <p style={{ 
                              margin: '2px 0 0 0', 
                              fontSize: '1rem', 
                              fontWeight: '700', 
                              color: isExpired ? '#dc2626' : remainingDays <= 7 ? '#f59e0b' : '#10b981' 
                            }}>
                              {isExpired ? 'Expired' : `${remainingDays} days left`}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Available Plans */}
                  <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '0.85rem', fontWeight: '600' }}>Available Plans</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
                    {/* Free Trial Plan */}
                    <div 
                      onClick={() => setSelectedPlan({ name: 'Free Trial', price: 0, duration: 7 })}
                      style={{
                        border: selectedPlan?.name === 'Free Trial' ? '2px solid #1e40af' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: selectedPlan?.name === 'Free Trial' ? '#eff6ff' : '#ffffff',
                        boxShadow: selectedPlan?.name === 'Free Trial' ? '0 2px 8px rgba(30, 64, 175, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.04)'
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <h5 style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>Free Trial</h5>
                        <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e40af', marginBottom: '4px' }}>₹0</div>
                        <p style={{ margin: '0', color: '#64748b', fontSize: '0.75rem', fontWeight: '500' }}>7 Days</p>
                      </div>
                    </div>

                    {/* 6 Months Plan */}
                    <div 
                      onClick={() => setSelectedPlan({ name: '6 Months Plan', price: 1499, duration: 180 })}
                      style={{
                        border: selectedPlan?.name === '6 Months Plan' ? '2px solid #1e40af' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: selectedPlan?.name === '6 Months Plan' ? '#eff6ff' : '#ffffff',
                        boxShadow: selectedPlan?.name === '6 Months Plan' ? '0 2px 8px rgba(30, 64, 175, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.04)',
                        position: 'relative'
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <h5 style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>6 Months</h5>
                        <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e40af', marginBottom: '4px' }}>₹1,499</div>
                        <p style={{ margin: '0', color: '#64748b', fontSize: '0.75rem', fontWeight: '500' }}>180 Days</p>
                      </div>
                    </div>

                    {/* 12 Months Plan */}
                    <div 
                      onClick={() => setSelectedPlan({ name: '12 Months Plan', price: 2999, duration: 365 })}
                      style={{
                        border: selectedPlan?.name === '12 Months Plan' ? '2px solid #1e40af' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: selectedPlan?.name === '12 Months Plan' ? '#eff6ff' : '#ffffff',
                        boxShadow: selectedPlan?.name === '12 Months Plan' ? '0 2px 8px rgba(30, 64, 175, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.04)',
                        position: 'relative'
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <h5 style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>12 Months</h5>
                        <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e40af', marginBottom: '4px' }}>₹2,999</div>
                        <p style={{ margin: '0', color: '#64748b', fontSize: '0.75rem', fontWeight: '500' }}>365 Days</p>
                      </div>
                    </div>
                  </div>

                  {/* Apply Button */}
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={handleApplyPlan}
                      disabled={!selectedPlan || applyingPlan}
                      className="btn btn-primary"
                      style={{
                        padding: '7px 24px',
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        opacity: (!selectedPlan || applyingPlan) ? 0.5 : 1,
                        cursor: (!selectedPlan || applyingPlan) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {applyingPlan ? 'Applying...' : selectedPlan ? `Apply ${selectedPlan.name}` : 'Select a Plan'}
                    </button>
                  </div>
                </div>
            </div>
          )}

          {/* Political Profile Tab */}
          {activeTab === 'political' && (
            <div className="profile-section">
              {Object.keys(politicalData).length > 0 ? (
                <div className="business-info-card">
                  <h3 className="business-info-title">Political Information</h3>
                  
                  <div className="business-info-content">
                    <div className="business-info-row">
                      <div className="business-info-field">
                        <span className="business-field-label">NAME</span>
                        <span className="business-field-value">{politicalData.name || 'Not provided'}</span>
                      </div>
                      <div className="business-info-field">
                        <span className="business-field-label">PARTY NAME</span>
                        <span className="business-field-value">{politicalData.party || 'Not provided'}</span>
                      </div>
                    </div>
                    
                    <div className="business-info-row">
                      <div className="business-info-field">
                        <span className="business-field-label">DESIGNATION</span>
                        <span className="business-field-value">{politicalData.designation || 'Not provided'}</span>
                      </div>
                      <div className="business-info-field">
                        <span className="business-field-label">MOBILE NUMBER</span>
                        <span className="business-field-value">{politicalData.mobile || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-profile">
                  <div className="no-profile-icon">🏛️</div>
                  <h4>No Political Profile</h4>
                  <p>No political profile data found.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
          duration={4000}
        />
      )}
    </div>
  );
};

export default UserProfile;
